import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Button } from 'components/Button/Button';
import { Dialog } from 'components/Dialog/Dialog';
import { SelectInput } from 'components/Inputs/SelectInput/SelectInput';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SummaryFrequency, SummarySchedule } from 'services/api/invoiceNotification/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';

import { DailyScheduleFields } from './DailyScheduleFields';
import { MonthlyScheduleFields } from './MonthlyScheduleFields';
import {
  formatTime,
  formatTimezone,
  getTimezones,
  SummaryScheduleFormState,
  TIME_SLOTS,
  toFormState,
  toSchedule,
} from './summarySchedule';
import {
  DialogActions,
  DialogBody,
  HelperText,
  Section,
  SectionLabel,
} from './SummaryScheduleDialog.style';

interface SummaryScheduleDialogProps {
  isOpen: boolean;
  schedule: SummarySchedule;
  timezone: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (schedule: SummarySchedule, timezone: string) => void;
}

export const SummaryScheduleDialog = ({
  isOpen,
  schedule,
  timezone,
  isSaving,
  onClose,
  onSave,
}: SummaryScheduleDialogProps): JSX.Element => {
  const { locale } = useIntl();
  const scheduleIntl = useIntlByKey('header.notification.schedule');
  const [form, setForm] = useState<SummaryScheduleFormState>(() => toFormState(schedule, timezone));
  const timezones = useMemo(getTimezones, []);

  // Start from the saved values every time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(schedule, timezone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when the dialog opens, not while the user edits
  }, [isOpen]);

  const update = (changes: Partial<SummaryScheduleFormState>) =>
    setForm((prev) => ({ ...prev, ...changes }));

  const hasNoDays = form.frequency === 'daily' && form.days.length === 0;
  const isValid = !hasNoDays && Boolean(form.timezone);

  const timeChoices = TIME_SLOTS.map((slot) => ({ label: formatTime(slot, locale), value: slot }));

  const handleSave = () => {
    if (isValid) {
      onSave(toSchedule(form), form.timezone);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={scheduleIntl('title')} showCloseIcon>
      <DialogBody>
        <Section>
          <SectionLabel>{scheduleIntl('frequency')}</SectionLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={form.frequency}
            onChange={(_, frequency: SummaryFrequency | null) => frequency && update({ frequency })}
          >
            <ToggleButton value="daily">{scheduleIntl('daily')}</ToggleButton>
            <ToggleButton value="monthly">{scheduleIntl('monthly')}</ToggleButton>
          </ToggleButtonGroup>
        </Section>

        {form.frequency === 'daily' ? (
          <DailyScheduleFields
            days={form.days}
            locale={locale}
            scheduleIntl={scheduleIntl}
            onChange={(days) => update({ days })}
          />
        ) : (
          <MonthlyScheduleFields
            form={form}
            locale={locale}
            scheduleIntl={scheduleIntl}
            onChange={update}
          />
        )}

        <Section>
          <SectionLabel>{scheduleIntl('time')}</SectionLabel>
          <SelectInput
            id="summary-time"
            width={160}
            preserveSpacingForHelperText={false}
            value={form.time}
            choices={timeChoices}
            onChange={(event) => update({ time: String(event.target.value) })}
          />
        </Section>

        <Section>
          <SectionLabel>{scheduleIntl('timezone')}</SectionLabel>
          <Autocomplete
            size="small"
            disableClearable
            options={timezones}
            value={form.timezone}
            getOptionLabel={formatTimezone}
            onChange={(_, value: string) => update({ timezone: value })}
            renderInput={(params) => <TextField {...params} />}
          />
        </Section>

        <HelperText>{scheduleIntl('empty-note')}</HelperText>

        <DialogActions>
          <Button displayStyle="unfilledGrey" onClick={onClose}>
            {scheduleIntl('cancel')}
          </Button>
          <Button
            displayStyle="filledOrange"
            onClick={handleSave}
            disabled={!isValid}
            isLoading={isSaving}
          >
            {scheduleIntl('save')}
          </Button>
        </DialogActions>
      </DialogBody>
    </Dialog>
  );
};
